/**
 * hopfFibration — visualize the Hopf fibration.
 *
 * The Hopf map S³ → S² is the only nontrivial fibration of any
 * sphere. Every point on the base S² is the image of a CIRCLE in
 * S³ (a "Hopf fiber"). The striking property: every two distinct
 * fibers are linked exactly once — like Borromean rings, but
 * pairwise. This is genuinely rare in art: most "fibration"
 * visualizations show trivial products like S² × S¹.
 *
 * We render the projection stereographically: take S³ as the unit
 * sphere in C², apply the Hopf map to land on S², then
 * stereographically project S² → the plane.
 *
 * Math: a point (z₁, z₂) ∈ C² with |z₁|² + |z₂|² = 1 maps to
 * the unit vector (2 Re(z₁z̄₂), 2 Im(z₁z̄₂), |z₁|² - |z₂|²) ∈ S².
 *
 * Each fiber is parameterised by α ∈ [0, 2π] via
 *   (z₁, z₂) = (cos(α) e^(iφ₁), sin(α) e^(iφ₂))
 * with φ₁ - φ₂ fixed (the "phase" of the fiber).
 *
 * We render 80 fibers at distinct base points tiling the disk
 * densely. Each fiber is a 32-point circle. The linked-circles
 * topology is visible when fibers cross. Depth comes from the
 * fiber's z-coordinate on the sphere.
 *
 * Reference: Milnor "Characteristic Classes"; Needham
 * "Visual Complex Analysis"; the recent Hanson/Hanson paper
 * on "Visualizing the Hopf Fibration" (2021).
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface HopfFibrationPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  bands: Float32Array;
}
const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
float bass; float mid; float high;

const float TAU = 6.283185307179586;
const float PI = 3.14159265358979;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// === Inverse stereographic projection from plane to S² ===
// Standard: a point (x, y) on the plane corresponds to
//   (X, Y, Z, W) = (2x, 2y, x² + y² - 1, x² + y² + 1)
// on the unit S³ in R⁴. Then (X, Y, Z, W) / W gives the
// stereographic normal to S².
// We just want the unit-sphere point (ux, uy, uz) ∈ S².
vec3 invStereoToS2(vec2 p) {
  float r2 = dot(p, p);
  vec3 s2 = vec3(2.0 * p.x, 2.0 * p.y, r2 - 1.0) / (r2 + 1.0);
  return s2;
}

// === Hopf fiber: sample points (s₃ᵢ, s₃ⱼ, s₃ₖ, s₃ₗ) in S³ ===
// Returns the stereographic projection of the 4D point's image
// under the Hopf map (which is on S²), then back to the plane.
//
// For a fiber with base (θ, φ) on S², the fiber in S³ is
//   (cos α · e^(iθ_α), sin α · e^(iφ_α))
// where θ_α = (θ + φ)/2 + α and φ_α = (θ - φ)/2, both mod 2π.
// So the parameter α traces a circle in S³ parameterized by t.
vec3 hopfFiberToPlane(float alpha, float theta, float phi, float t) {
  // The fiber parameter (the "phase" of the great circle).
  float phase = (theta + phi) * 0.5 + alpha + t * 0.1;
  // Construct the (z₁, z₂) ∈ C² point on the fiber.
  float ca = cos(alpha), sa = sin(alpha);
  vec2 z1 = ca * vec2(cos(phase), sin(phase));
  vec2 z2 = sa * vec2(cos(phase - (theta - phi)), sin(phase - (theta - phi)));
  // Hopf map: (z₁, z₂) → (2 Re(z₁z̄₂), 2 Im(z₁z̄₂), |z₁|² - |z₂|²).
  vec2 z1z2bar = vec2(
    z1.x * z2.x + z1.y * z2.y,
    z1.y * z2.x - z1.x * z2.y
  );
  vec3 s2 = vec3(2.0 * z1z2bar.x, 2.0 * z1z2bar.y, ca * ca - sa * sa);
  // Open the sphere just to be safe (should already be on S²).
  return s2 / max(length(s2), 1e-4);
}

// Approximate "closest distance on a fiber" by sampling. For
// 32 sample points per fiber, with 80 fibers, that's 2560
// evaluations per pixel — affordable on a GPU but heavy.
// We do this in a single inner loop below.
float minDistToFiber(vec2 uv, float theta, float phi, float t, out float bestDepth) {
  float minD = 1e3;
  bestDepth = 0.0;
  for (int k = 0; k < 32; k++) {
    float fk = float(k);
    float alpha = fk / 32.0 * TAU;
    vec3 s2 = hopfFiberToPlane(alpha, theta, phi, t);
    // Project S² to the plane via stereographic from the north pole.
    // For a unit S², stereographic from (0,0,1): (x, y)/(1 - z).
    float denom = 1.0 - s2.z;
    if (abs(denom) < 1e-3) denom = 1e-3 * sign(denom + 1e-9);
    vec2 plane = s2.xy / denom;
    float d = length(uv - plane);
    if (d < minD) {
      minD = d;
      bestDepth = s2.z;  // depth from -1 (south) to 1 (north)
    }
  }
  return minD;
}

vec3 iridescent(float t) {
  return 0.5 + 0.5 * cos(6.28318530718 * (t + vec3(0.0, 0.33, 0.67)));
}

void main() {
  vec2 uv = (vUV - 0.5) * 2.0;
  uv.x *= uResolution.x / uResolution.y;

  float T = uTime;
  float r = length(uv);

  // 80 fibers. Each has a base point on S² sampled via Fibonacci
  // for even distribution on the sphere.
  vec3 col = vec3(0.0);
  float fiberCount = 0.0;

  // Pre-compute the temp variables outside the loop.
  float closestD = 100.0;
  float closestDepth = 0.0;
  float closestFiberIdx = 0.0;

  // Sample 80 fibers. We can't easily identify which one is
  // "closest" in a single pass without per-pixel sorting, so we
  // approximate: for each fiber, check the distance and bias
  // toward fibers whose closest point is very close.
  for (int i = 0; i < 80; i++) {
    float fi = float(i);
    // Fibonacci sphere distribution.
    float golden = (1.0 + sqrt(5.0)) * 0.5;
    float phi = acos(1.0 - 2.0 * (fi + 0.5) / 80.0);
    float theta = TAU * fi / golden;
    vec2 baseS2 = vec2(sin(phi) * cos(theta), sin(phi) * sin(theta));
    // Add a slow precession of the base sphere.
    float pre = T * 0.07;
    baseS2 = vec2(
      baseS2.x * cos(pre) - baseS2.y * sin(pre),
      baseS2.x * sin(pre) + baseS2.y * cos(pre)
    );
    // Don't try to push past the disk boundary.
    float dToCenter = length(baseS2);
    if (dToCenter > 0.95) continue;

    float depth;
    // Convert baseS2 → (theta, phi) on S².
    float s2Theta = atan(baseS2.y, baseS2.x);
    float s2Phi = length(baseS2);
    float d = minDistToFiber(uv, s2Theta, s2Phi, T, depth);
    if (d < closestD) {
      closestD = d;
      closestDepth = depth;
      closestFiberIdx = fi;
    }
  }

  // === Fiber rendering ===
  // The closest fiber is what we draw. We render it as a sharp
  // line with a soft halo, depth-tinted by its z-coordinate.
  // We also render a SECOND TIER: the 2nd and 3rd closest fibers
  // contribute a faint glow, so the linking topology shows.
  float lineMask = exp(-closestD * 60.0);
  float haloMask = exp(-closestD * 8.0);
  // Depth color: high latitudes (north pole) = warm, low (south) = cool.
  vec3 depthCol = mix(
    vec3(0.2, 0.4, 0.95),    // south pole: blue
    vec3(0.95, 0.4, 0.2),    // north pole: orange
    smoothstep(-0.5, 0.5, closestDepth)
  );
  // Hue from fiber index (so different fibers get different colors
  // when they cross).
  vec3 fiberCol = iridescent(closestFiberIdx * 0.137 + T * 0.05);
  // Mix depth color and fiber hue.
  vec3 colLine = mix(depthCol, fiberCol, 0.6);
  col += colLine * lineMask * 1.5;
  col += colLine * haloMask * 0.35;

  // === Background: the Poincaré disk at infinity ===
  // The disk's boundary is the "sphere at infinity" of the
  // Poincaré model — bright it slightly to suggest this.
  float diskBorder = smoothstep(0.95, 1.0, r);
  vec3 infinityCol = vec3(0.85, 0.5, 0.95);
  col += infinityCol * diskBorder * 0.4;

  // Center: a faint glow at the "north pole" of the disk.
  float centerGlow = exp(-r * 2.0);
  col += vec3(0.20, 0.10, 0.30) * centerGlow * 0.6;

  // Random density of small bright dots that look like additional
  // fibers sampled at extreme depths.
  float dots = 0.0;
  for (int i = 0; i < 16; i++) {
    float fi = float(i);
    float a = hash21(vec2(fi, 1.0)) * TAU;
    float rad = 0.2 + 0.7 * hash21(vec2(fi, 7.0));
    vec2 dotPos = vec2(cos(a), sin(a)) * rad;
    float d = length(uv - dotPos);
    dots += exp(-d * d * 5000.0);
  }
  col += vec3(0.95, 0.85, 0.95) * dots * 0.3;

  // === Fade outside the disk ===
  col = mix(vec3(0.02, 0.04, 0.12), col, smoothstep(1.4, 0.95, r));

  // === Bass pulse ===
  col *= 0.9 + 0.1 * bass;

  // === Vignette ===
  float vig = 1.0 - dot(uv, uv) * 0.25;
  col *= clamp(vig, 0.0, 1.0);

  // === Tone mapping + gamma ===
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeHopfFibrationBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeHopfFibrationRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}
export function createHopfFibrationPipeline(gl: WebGL2RenderingContext): HopfFibrationPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return { program, vao, vbo, bands: new Float32Array(9) };
}
export function drawHopfFibration(gl: WebGL2RenderingContext, p: HopfFibrationPipeline, w: number, h: number, t: number): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);
  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
export { VERT_SRC, FRAG_SRC };
